import { IBuilderStorage } from "./Plugins/BuilderStorage/IBuilderStorage";
import { IFormAssignmentPlugin } from "./Plugins/Assignment/IFormAssignmentPlugin";
import { IFlowExecutionPlugin } from "./Plugins/FlowExecution/IFlowExecutionPlugin";
import { IDataStoreFactory } from "./Plugins/DataStorage/IDataStoreFactory";
import { IFileStore } from "./Plugins/FileStorage/IFileStore";
import { ILog } from "./Plugins/Logging/ILog";
import { ConsoleLogProvider } from "./Plugins/Logging/ConsoleLogProvider";
import { CompositeLogProvider } from "./Plugins/Logging/CompositeLogProvider";
import { LogLevel } from "./Plugins/Logging/LogLevel";
import { IFlowStepPlugin } from "./Plugins/FlowStep/IFlowStepPlugin";
import { IUserDataStorage } from "./Plugins/UserDataStorage/IUserDataStorage";
import { UserDataStorage } from "./Plugins/UserDataStorage/UserDataStorage";
import { BuilderStorageImpl } from "./Plugins/BuilderStorage/BuilderStorageImpl";
import { ILeasingPlugin } from "./Plugins/Leasing/ILeasingPlugin";
import { LeasingPlugin } from "./Plugins/Leasing/LeasingPlugin";
import { DefaultFlowStepPlugin } from "./Plugins/FlowStep/DefaultFlowStepPlugin";
import { FlowExecutionPluginImpl } from "./Plugins/FlowExecution/FlowExecutionPluginImpl";
import { DefaultUserContextPlugin, IUserContextPlugin } from "./Plugins/UserContext/UserContextPlugin";
import { IOpenAIPlugin } from "./Plugins/OpenAPI/IOpenAIPlugin";
import { OpenAIPlugin } from "./Plugins/OpenAPI/OpenAIPlugin";
import { FormAssignmentPlugin } from "./Plugins/Assignment/FormAssignmentPlugin";
import { TestDataStoreFactory } from "./Plugins/DataStorage/TestDataStoreFactory";

export class AppGlueCorePlugins {
    static pluginsChecked: boolean | undefined = undefined;
    static compositeProvider: CompositeLogProvider | undefined;
    static fontList: string[] = [
        "Arial",
        "Verdana",
        "Tahoma",
        "Trebuchet MS",
        "Times New Roman",
        "Georgia",
        "Garamond",
        "Courier New",
        "Brush Script MT",
    ];
    static defaultFont = this.fontList[0];
    private static _logProviders: ILog[] = [new ConsoleLogProvider()];
    private static _userStoragePlugin: IUserDataStorage | undefined = undefined;
    private static _userContext: IUserContextPlugin;
    private static _openAIPlugin?: IOpenAIPlugin;

    private static _OPEN_AI_KEY?: string;

    static get OPEN_AI_KEY(): string {
        return this._OPEN_AI_KEY ?? "";
    }

    static set OPEN_AI_KEY(value: string) {
        this._OPEN_AI_KEY = value;
    }

    static get OpenAIPlugin(): IOpenAIPlugin {
        if (!this._openAIPlugin) return new OpenAIPlugin(this.OPEN_AI_KEY);

        this._openAIPlugin.apiKey = this.OPEN_AI_KEY;

        return this._openAIPlugin;
    }

    static set OpenAIPlugin(value: IOpenAIPlugin) {
        if (!value) return;
        this._openAIPlugin = value;
    }

    static get UserContext(): IUserContextPlugin {
        if (!this._userContext) return new DefaultUserContextPlugin();
        return this._userContext;
    }

    static set UserContext(value: IUserContextPlugin) {
        this._userContext = value;
    }

    private static _leasingPlugin: ILeasingPlugin | undefined;

    static get LeasingPlugin(): ILeasingPlugin | undefined {
        if (this._leasingPlugin) return this._leasingPlugin;

        if (this._DataStoreFactory) return new LeasingPlugin();

        return undefined;
    }

    static set LeasingPlugin(value: ILeasingPlugin | undefined) {
        this._leasingPlugin = value;
    }

    private static _AssignmentStorage: IFormAssignmentPlugin | undefined;

    static get AssignmentStorage(): IFormAssignmentPlugin | undefined {
        if (!this._AssignmentStorage) return new FormAssignmentPlugin();
        return this._AssignmentStorage;
    }

    static set AssignmentStorage(value: IFormAssignmentPlugin | undefined) {
        // cannot unset
        if (!value) return;
        this._AssignmentStorage = value;
    }

    private static _FileStorage: IFileStore | undefined = undefined;

    static get FileStorage(): IFileStore | undefined {
        return this._FileStorage;
    }

    static set FileStorage(value: IFileStore | undefined) {
        // cannot unset
        if (!value) return;
        this._FileStorage = value;
    }

    private static _BuilderStorage: IBuilderStorage | undefined = undefined;

    static get BuilderStorage(): IBuilderStorage | undefined {
        if (this._BuilderStorage) return this._BuilderStorage;

        if (this._DataStoreFactory) return new BuilderStorageImpl();

        return undefined;
    }

    static set BuilderStorage(value: IBuilderStorage | undefined) {
        this._BuilderStorage = value;
    }

    private static _FlowExecutionPlugin: IFlowExecutionPlugin | undefined = undefined;

    static get FlowExecutionPlugin(): IFlowExecutionPlugin | undefined {
        return this._FlowExecutionPlugin ?? new FlowExecutionPluginImpl();
    }

    static set FlowExecutionPlugin(value: IFlowExecutionPlugin | undefined) {
        // cannot unset
        if (!value) return;
        this._FlowExecutionPlugin = value;
    }

    private static _DataStoreFactory: IDataStoreFactory | undefined = undefined;

    static get DataStoreFactory(): IDataStoreFactory | undefined {
        if (this._DataStoreFactory) return new TestDataStoreFactory();
        return this._DataStoreFactory;
    }

    static set DataStoreFactory(value: IDataStoreFactory | undefined) {
        this._DataStoreFactory = value;
    }

    private static _FlowStepPlugin: IFlowStepPlugin | undefined;

    static get FlowStepPlugin(): IFlowStepPlugin {
        if (!this._FlowStepPlugin) return new DefaultFlowStepPlugin();
        return this._FlowStepPlugin;
    }

    static set FlowStepPlugin(value: IFlowStepPlugin | undefined) {
        this._FlowStepPlugin = value;
    }

    private static _loggingLevels: LogLevel[] = [LogLevel.ERROR, LogLevel.WARN, LogLevel.INFO];

    static get loggingLevels(): LogLevel[] {
        return this._loggingLevels;
    }

    static set loggingLevels(value: LogLevel[]) {
        this._loggingLevels = value;
        this.compositeProvider = undefined;
    }

    static get logger(): ILog {
        if (!this.compositeProvider)
            this.compositeProvider = new CompositeLogProvider(this._logProviders, this._loggingLevels);
        return this.compositeProvider;
    }

    static get UserStoragePlugin(): IUserDataStorage | undefined {
        if (this._userStoragePlugin) return this._userStoragePlugin;

        if (this._DataStoreFactory) return new UserDataStorage();

        return undefined;
    }

    static set UserStoragePlugin(value: IUserDataStorage | undefined) {
        this._userStoragePlugin = value;
    }

    static addLogProvider(log: ILog) {
        this._logProviders.push(log);
        this.compositeProvider = undefined;
    }

    static validatePlugins() {
        // throws error if a plugin is missing.
    }
}
